<template>
  <div class="billing-table">
    <InvoiceDataModal ref="invoiceDataModal" />

    <div class="billing-table__header">
      <div class="billing-table__year-box">
        <span class="billing-table__year">{{ year }}</span>
      </div>

      <ul class="billing-table__tabs">
        <li
          class="arrow-action"
          @click="previousMonth"
        >
          <svg-loader
            src="/static/images/arrow-up.svg"
            class="billing-table__arrow billing-table__arrow--right"
          />
        </li>
        <li
          v-for="(month, indexMonth) in months"
          :key="indexMonth"
          :class="['billing-table__tab', { 'is-active': activeMonth === indexMonth }]"
          @click="setActiveMonth(indexMonth)"
        >
          {{ month }}
        </li>
        <li
          class="arrow-action"
          @click="nextMonth"
        >
          <svg-loader
            src="/static/images/arrow-up.svg"
            class="billing-table__arrow billing-table__arrow--left"
          />
        </li>
      </ul>
    </div>

    <!-- <div class="billing-table__subtitle">
      Faturamento com fechamento em: {{ closingDate }}
    </div> -->

    <div class="billing-table__table-header">
      <div
        v-for="(header, index) in tableHeaders"
        :key="index"
        class="billing-table__th"
        @click="orderByColumn(header.key, index)"
      >
        {{ header.label }}
        <md-tooltip
          v-if="header.key === 'provisionalTax'"
          md-direction="top"
        >
          Imposto cobrado antes do faturamento
        </md-tooltip>

        <svg-loader
          src="/static/images/icon-sort-arrow.svg"
          class="arrow-direction"
          :class="{
            'arrow-direction__down': actualColumnSortType === index && sortDirection === 'asc',
            'arrow-direction__up': actualColumnSortType === index && sortDirection === 'desc'
          }"
        />
      </div>
      <div class="billing-table__th" />
    </div>

    <template v-if="localBillingsList && localBillingsList.length">
      <div
        v-for="(row, rowIndex) in localBillingsList"
        :key="rowIndex"
        class="billing-table__table-row"
        :class="{ active: activeItem === rowIndex }"
      >
        <div
          class="content"
          @click="setItemOpened(rowIndex)"
        >
          <div
            v-for="(header, colIndex) in tableHeaders"
            :key="colIndex"
            class="billing-table__td"
          >
            {{ formatData(header.key, row) }}
          </div>

          <div class="billing-table__td billing-table__td--download">
            <div class="box-download">
              <div
                class="status-icon icon-eye-2"
                :class="{ 'is-disabled': !row.billingId }"
                @click="row.billingId && $router.push(`/faturamento/${row.billingId}/${months[activeMonth]}/${year}`)"
              >
                <md-tooltip md-direction="top">
                  Visualizar fatura
                </md-tooltip>
              </div>
              <div
                class="status-icon icon-download-pdf"
                @click="download(row.invoiceNumber, 'pdf', $event)"
              >
                <md-tooltip md-direction="top">
                  Exportar PDF
                </md-tooltip>
              </div>
              <div
                v-if="row.invoiceCSV"
                class="status-icon icon-download-csv"
                @click="download(row.invoiceNumber, 'csv', $event)"
              >
                <md-tooltip md-direction="top">
                  Exportar CSV
                </md-tooltip>
              </div>
              <div
                v-if="row.invoiceXML"
                class="status-icon icon-download-xml"
                @click="download(row.invoiceNumber, 'xml', $event)"
              >
                <md-tooltip md-direction="top">
                  Exportar XML
                </md-tooltip>
              </div>
            </div>
            <svg-loader src="/static/images/arrow-up.svg" />
          </div>
        </div>

        <div
          v-if="activeItem === rowIndex"
          class="billing-table__sub-table"
        >
          <div class="sub-table-content head">
            <p class="--note-number">
              N. da nota
            </p>
            <p>Valor</p>
            <p v-if="!toggle.prePaid">
              Vencimento
            </p>
            <p
              v-if="!toggle.prePaid"
              class="--ticket-number"
            >
              N. do Boleto
            </p>
            <p class="--download">
              {{ toggle.prePaid ? 'Nfe' : 'Nfe / Boleto' }}
            </p>
          </div>
          <div
            v-for="(item, i) in row.items"
            :key="i"
            class="sub-table-content"
          >
            <p class="--note-number">
              {{ item.noteNumber }}
            </p>
            <p>{{ price(item.value) }}</p>
            <p v-if="!toggle.prePaid">
              {{ item.pay }}
            </p>
            <p
              v-if="!toggle.prePaid"
              class="--ticket-number"
            >
              {{ item.ticketNumber }}
            </p>
            <div class="--download">
              <div
                v-if="item.noteNumber"
                class="status-icon icon-nfe-2"
                @click="downloadNote(item)"
              >
                <md-tooltip md-direction="top">
                  Imprimir Nota Fiscal
                </md-tooltip>
              </div>
              <div
                v-if="item.ticketNumber && !toggle.prePaid"
                class="status-icon icon-bill"
                @click="downloadTicket(item.ticketNumber)"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
    <div v-else>
      <p class="billing-table__empty-message">
        Ainda não há Faturas pra serem visualizadas
      </p>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { justDownloadBase64File } from '@/utils/convertBase64ToFile'
import { formatPrice } from '@/utils/formatPrice'

import billings from '@/api/billings'
import notify from '@/api/notification'

import Spinner from '@/components/common/Spinner'
import InvoiceDataModal from '@/components/Invoicing/components/InvoiceDataModal.vue'

export default {
  name: 'BillingsListTable',
  components: {
    Spinner,
    InvoiceDataModal
  },
  props: {
    year: {
      type: Number,
      default: new Date().getFullYear()
    },
    closingDate: {
      type: String,
      default: ''
    },
    tableHeaders: {
      type: Array,
      default: () => [
        { key: 'invoiceDate', label: 'DATA' },
        { key: 'invoiceNumber', label: 'N. FATURA' },
        { key: 'invoiceEmission', label: 'EMISSÃO' },
        { key: 'chargedTax', label: 'IMPOSTO COBRADO' },
        { key: 'provisionalTax', label: 'IMPOSTO PROVI.' },
        { key: 'invoiceTotal', label: 'VALOR' }
      ]
    },
    tableData: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      actualColumnSortType: null,
      sortDirection: null,
      activeMonth: 0,
      months: ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'],
      activeItem: null,
      currentPage: 0,
      isDoingDownloading: false,
      localBillingsList: []
    }
  },
  computed: {
    ...mapGetters(['billingsList', 'isLoadingBillings', 'billingsTotalPages', 'toggle'])
  },
  watch: {
    billingsList: {
      immediate: true,
      handler(newList) {
        this.localBillingsList = [...newList]
      }
    }
  },
  created() {
    const currentMonth = new Date().getMonth();
    this.activeMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    if (currentMonth === 0) {
      this.year -= 1;
    }
    this.loadMore(this.currentPage)
    window.addEventListener('scroll', this.handleScroll)
  },
  destroyed() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    ...mapActions(['getInvoiceList', 'cleanInvoiceList']),
    formatData(key, row) {
      if (key === 'invoiceTotal') {
        return this.price(row[key])
      }

      if (key === 'invoiceEmission') {
        return new Date(row[key]).toLocaleDateString('pt-BR')
      }

      return row[key] || '-'
    },
    setActiveMonth(index) {
      this.activeMonth = index
      this.currentPage = 0
      this.cleanInvoiceList()
      this.loadMore()
    },
    nextMonth() {
      if (this.activeMonth === 11) {
        this.year += 1
        this.activeMonth = 0
      } else {
        this.activeMonth += 1
      }
      this.currentPage = 0
      this.cleanInvoiceList()
      this.loadMore()
    },
    previousMonth() {
      if (this.activeMonth === 0) {
        this.year -= 1
        this.activeMonth = 11
      } else {
        this.activeMonth -= 1
      }
      this.currentPage = 0
      this.cleanInvoiceList()
      this.loadMore()
    },
    orderByColumn(column, index) {
      const sortType = this.actualColumnSortType === index ? (this.sortDirection === 'asc' ? 'desc' : 'asc') : 'desc'

      this.sortDirection = sortType
      this.actualColumnSortType = index

      const sortedList = [...this.localBillingsList].sort((a, b) => {
        const valueA = a[column]
        const valueB = b[column]

        const isNumericA = !isNaN(parseFloat(valueA)) && isFinite(valueA)
        const isNumericB = !isNaN(parseFloat(valueB)) && isFinite(valueB)

        if (isNumericA && isNumericB) return sortType === 'asc' ? valueA - valueB : valueB - valueA
        else if (isNumericA) return sortType === 'asc' ? -1 : 1
        else if (isNumericB) return sortType === 'asc' ? 1 : -1
        else return sortType === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA)
      })

      this.localBillingsList = sortedList
    },
    price(value) {
      if (!isNaN(value)) {
        return `R$ ${formatPrice(value)}`
      }
      return ''
    },
    setItemOpened(index) {
      if (this.activeItem === index) {
        return (this.activeItem = null)
      }
      return (this.activeItem = index)
    },
    download(number, type, event) {
      this.isDoingDownloading = true
      event.stopPropagation()

      let typeDownload = ''
      if (type === 'csv') {
        typeDownload = 'CSV'
      } else if (type === 'pdf') {
        typeDownload = 'Pdf'
      } else if (type === 'xml') {
        typeDownload = 'XML'
      }

      const data = { number: number, type: typeDownload }

      billings
        .downloadInvoice(data)
        .then(res => {
          justDownloadBase64File(`application/${type}`, res.data.base64, `fatura_${res.data.invoiceNumber}.${type}`)
          this.isDoingDownloading = false
        })
        .catch(err => {
          this.isDoingDownloading = false
          notify({
            title: 'AVISO: ',
            message: 'Ocorreu um erro ao baixar a fatura.'
          })
          console.error(err)
        })
    },
    downloadNote(noteData) {
      this.$refs.invoiceDataModal.openInvoiceDataModal(noteData)
    },
    downloadTicket(ticketNumber) {
      const walletType = this.toggle.prePaid ? 'PREPAID' : 'POSTPAID'

      billings
        .getTicketData(ticketNumber, walletType)
        .then(res => {
          window.open(
            `https://www63.bb.com.br/portalbb/boleto/boletos/hc21e,802,3322,10343,2,0,1.bbx?lnDigtvl=${res.data.ticketNumberNotVerifyDigit}&cpfCnpjPagador=${res.data.withdrawnCNPJ}&cpfCnpjbeneficiario=${res.data.assignorCNPJ}`,
            '_blank'
          )
        })
        .catch(err => {
          notify({
            title: 'AVISO: ',
            message: 'Erro ao carregar o boleto.'
          })
          console.error(err)
        })
    },
    handleScroll() {
      const scrollPosition = window.scrollY + window.innerHeight
      const pageHeight = document.documentElement.scrollHeight
      if (scrollPosition >= pageHeight) {
        this.loadMore(this.currentPage)
      }
    },
    loadMore() {
      if (this.currentPage < this.billingsTotalPages) {
        this.getInvoiceList({
          page: this.currentPage,
          referenceDate: `${this.year}-${String(this.activeMonth + 1).padStart(2, '0')}`
        }).then(() => {
          if (this.billingsList.length) {
            this.currentPage++
          }
        })
      }
    }
  }
}
</script>

<style lang="scss">
.billing-table {
  background-color: #ededed;
  margin: 24px 0;

  .is-disabled {
    pointer-events: none;
    opacity: 0.6;
  }

  &__header {
    display: flex;
    align-items: center;
  }

  &__year-box {
    display: flex;
    align-items: center;
    background-color: #fff;
    padding: 6px 12px;
    border-radius: 4px 4px 0 0;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 72px;
  }

  &__arrow {
    cursor: pointer;
    font-size: 8px;
    width: 18px;
    min-width: 18px;
    height: 18px;
    min-height: 18px;

    &--left {
      transform: rotate(90deg);
      margin-right: 8px;
    }

    &--right {
      transform: rotate(-90deg);
      margin-left: 8px;
    }
  }

  &__year {
    font-weight: bold;
    font-size: 14px;
    color: #666;
  }

  &__tabs {
    display: flex;
    gap: 8px;
    list-style: none;
    margin: 0;
    padding: 0;
    flex: 1;

    .arrow-action {
      display: flex;
      align-items: center;
      justify-items: center;
      cursor: pointer;
      margin: 0;
    }

    .billing-table__tab {
      background-color: #e6e6e6;
      padding: 6px 12px;
      border-radius: 4px 4px 0 0;
      cursor: pointer;
      font-size: 12px;
      text-transform: uppercase;
      color: #666666;
      flex: 1;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 600;
      letter-spacing: 1px;
      margin: 0;

      &.is-active {
        background-color: #fff;
        color: #666;
      }
    }
  }

  &__subtitle {
    background-color: #f7f7f7;
    padding: 8px 12px;
    font-size: 12px;
    color: #333;
    height: 48px;
    display: flex;
    align-items: center;
  }

  &__table-header {
    display: flex;
    background-color: #fff;
    padding: 0 16px;
    border-radius: 4px 4px 0 0;

    .billing-table__th {
      flex: 1;
      font-size: 11px;
      font-weight: 900;
      text-transform: uppercase;
      color: #717171;
      padding: 8px;
      display: flex;
      align-items: center;
      border-bottom: 2px solid #797979;
    }

    .arrow-direction {
      margin: 0;
      margin-left: 4px;
      width: 13px;
      min-width: 13px;

      svg {
        width: 13px;
      }

      &__up {
        .icon-sort-arrow-up {
          fill: #dbdbdb;
        }
      }

      &__down {
        .icon-sort-arrow-down {
          fill: #dbdbdb;
        }
      }
    }
  }

  &__table-row {
    display: flex;
    background-color: #fff;
    border-bottom: 1px solid #eee;
    padding: 0 16px;
    flex-direction: column;

    &.active {
      .content {
        margin-bottom: 8px;

        .billing-table__td--download .md-icon {
          transform: rotate(0deg);
        }
      }

      .billing-table__sub-table {
        opacity: 1;
        max-height: fit-content;

        .sub-table-content {
          display: flex;
        }
      }
    }

    &:last-child {
      border-radius: 0 0 4px 4px;
      border-bottom: none;
    }

    .content {
      display: flex;
      font-weight: 400;
      cursor: pointer;

      p {
        font-size: 14px;
        color: #363636;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .billing-table__td--download {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .md-icon {
          margin: 0;
          transform: rotate(180deg);
          transition: all 0.2s ease-in;
        }

        .box-download {
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .status-icon {
          margin: 0 3px;
        }
      }
    }

    .billing-table__td {
      flex: 1;
      padding: 8px;
      font-size: 13px;
      color: #797979;
      display: flex;
      align-items: center;

      &--download {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .md-icon {
          margin: 0;
          transform: rotate(180deg);
          transition: all 0.2s ease-in;
        }

        .box-download {
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .status-icon {
          margin: 0 3px;
        }
      }
    }
  }

  &__sub-table {
    max-height: 0;
    opacity: 0;
    transition: max-height 0.3s ease, opacity 0.3s ease;

    &.active {
      max-height: fit-content;
      opacity: 1;
    }

    .sub-table-content {
      display: flex;
      padding: 10px 24px;
      background-color: #f3f3f3;

      &.head {
        font-weight: bold;
        text-transform: uppercase;
        font-size: 12px;
      }

      p {
        flex: 1;
        margin: 0;
        color: #666;

        &.--note-number {
          flex: 1;
        }

        &.--download {
          flex: inherit;
          width: 116px;
        }
      }

      .--note-number {
        flex: 2;
      }

      .--download {
        display: flex;
        align-items: center;
        width: 116px;

        .status-icon {
          margin: 0;
          margin-right: 8px;
          cursor: pointer;
        }
      }
    }
  }

  &__empty-message {
    background: #fff;
    padding: 16px;
    text-align: center;
    border-radius: 0 0 4px 4px;
  }
}
</style>
